import { useState } from "react";
import { toast } from "sonner";
import { Loader2, CalendarHeart, Flame, Gift } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal, Overline } from "@/components/Reveal";

const CONCERNS = [
  "Prophetic direction & a word from the Lord",
  "Deliverance (bondage, curses, roots, spells, witchcraft)",
  "Spiritual breakthrough",
  "Financial breakthrough",
  "Healing",
  "Other",
];

const CASHAPP_URL = "https://cash.app/$prophetmckuhn1";

export const Appointment = () => {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", preferred_date: "", preferred_time: "",
    concern: CONCERNS[0], details: "",
  });
  const [busy, setBusy] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please share your name and email.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/appointment", form);
      toast.success("Your appointment request has been received. Prophet McKuhn will be in touch.");
      setForm({ name: "", email: "", phone: "", preferred_date: "", preferred_time: "", concern: CONCERNS[0], details: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const field = "w-full px-4 py-3 rounded-xl bg-ink border border-white/15 text-parchment placeholder:text-parchment/40 focus:outline-none focus:ring-2 focus:ring-gold";
  const lbl = "block text-sm font-medium mb-1.5 text-parchment/80";

  return (
    <section
      id="appointment"
      data-testid="appointment-section"
      className="relative py-24 sm:py-32 bg-ink text-parchment grain overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-80"
        style={{
          background:
            "radial-gradient(circle at 80% 15%, rgba(197,80,20,0.18), transparent 45%), radial-gradient(circle at 10% 90%, rgba(197,160,89,0.12), transparent 45%)",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="flex items-center gap-3">
              <Flame className="text-orange-400" size={20} />
              <Overline>Prophetic Intercession</Overline>
            </div>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
              Book a Prophetic Intercessory Appointment
            </h2>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-6 space-y-5 text-base sm:text-lg leading-relaxed text-parchment/80">
              <p>
                Viewers and followers are invited to schedule a private prophetic
                intercessory appointment, that the man of God, Prophet McKuhn, may
                prayerfully share whatever the Spirit reveals concerning your life
                and direction.
              </p>
              <p>
                If you believe you are bound, accursed, or that a root, spell, or
                witchcraft has been set against you — or if you are believing God
                for a spiritual or financial breakthrough — reach out to Prophet
                McKuhn. The Holy Ghost within him has been appointed for this
                season to bring about a change in the Ecclesia, the Church.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="mt-8 p-6 rounded-2xl border border-gold/30 bg-white/[0.04]">
              <p className="text-sm text-parchment/80 leading-relaxed">
                Sow a seed today and believe by faith that whatsoever you have
                prayed shall come to pass, in Christ Jesus — Yahshua. Amen.
              </p>
              <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-parchment/50">Cash App</p>
              <div className="mt-1 flex items-center justify-between gap-4 flex-wrap">
                <span className="font-serif text-2xl font-semibold gold-foil">$prophetmckuhn1</span>
                <a
                  data-testid="appointment-cashapp"
                  href={CASHAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gold text-ink text-sm font-semibold hover:bg-gold-light transition-colors"
                >
                  <Gift size={16} /> Sow a Seed
                </a>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <form
              data-testid="appointment-form"
              onSubmit={submit}
              className="p-7 sm:p-9 rounded-3xl border border-gold/20 bg-white/[0.04] backdrop-blur-sm"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className={lbl}>Name</label>
                  <input data-testid="appt-name" className={field} value={form.name} onChange={update("name")} placeholder="Your full name" />
                </div>
                <div>
                  <label className={lbl}>Email</label>
                  <input data-testid="appt-email" type="email" className={field} value={form.email} onChange={update("email")} placeholder="you@email.com" />
                </div>
                <div>
                  <label className={lbl}>Phone (optional)</label>
                  <input data-testid="appt-phone" className={field} value={form.phone} onChange={update("phone")} placeholder="(xxx) xxx-xxxx" />
                </div>
                <div>
                  <label className={lbl}>Area of need</label>
                  <select data-testid="appt-concern" className={field} value={form.concern} onChange={update("concern")}>
                    {CONCERNS.map((c) => (
                      <option key={c} value={c} className="bg-ink">{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={lbl}>Preferred date</label>
                  <input data-testid="appt-date" type="date" className={field} value={form.preferred_date} onChange={update("preferred_date")} />
                </div>
                <div>
                  <label className={lbl}>Preferred time</label>
                  <input data-testid="appt-time" className={field} value={form.preferred_time} onChange={update("preferred_time")} placeholder="e.g. Evenings" />
                </div>
              </div>
              <div className="mt-5">
                <label className={lbl}>Share your heart (what are you believing God for?)</label>
                <textarea data-testid="appt-details" rows={5} className={`${field} resize-none`} value={form.details} onChange={update("details")} placeholder="Tell Prophet McKuhn how he can stand in prayer with you…" />
              </div>
              <button
                data-testid="appointment-submit"
                type="submit"
                disabled={busy}
                className="mt-7 inline-flex items-center gap-2 px-7 py-3 rounded-full bg-gold text-ink font-semibold hover:bg-gold-light transition-colors disabled:opacity-60"
              >
                {busy ? <Loader2 size={18} className="animate-spin" /> : <CalendarHeart size={18} />}
                Request Appointment
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
